import React from 'react';
import { useEffect, useState, useRef } from 'react';
import './Notification.css';
import './Notification2.css';
import './Notification3.css';
import './Notification4.css';
import DatePicker from 'react-datepicker';
import * as XLSX from 'xlsx';
import { FaSearch, FaDownload, FaFilter, FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import { createPortal } from 'react-dom';

// If you still need the floor plan image, import it:
// import groundFloor from './images/YTMapNew.png';

export default function Notification() {
    const [allRawData, setAllRawData] = useState([]);     // all records (client‑side cache)
    const [filteredData, setFilteredData] = useState([]); // filtered result
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedType, setSelectedType] = useState('0');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const itemsPerPage = 10;

    // ---- API configuration ----
    const API_BASE = 'https://futipiot.ddns.net';

    // ---- Load all records on mount ----
    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            try {
                const httpClient = axios.create({
                    baseURL: API_BASE,
                    headers: { 'Content-Type': 'application/json' }
                });
                // Get total count
                const countRes = await httpClient.get('/api/futippastrecord/count');
                const totalRecords = countRes.data;
                // Fetch in chunks of 100 (adjust as needed)
                const chunkSize = 100;
                const totalPages = Math.ceil(totalRecords / chunkSize);
                let allData = [];
                for (let page = 1; page <= totalPages; page++) {
                    const res = await httpClient.get(`/api/futippastrecord/${page}/${chunkSize}/`);
                    const mapped = res.data.map(item => ({
                        Id: item.item,
                        Label: item.label,
                        Status: item.status,
                        DataType: item.dataType,
                        Timestamp: item.timestamp,
                        Acknowledge: item.acknowledge,
                        Name: item.name
                    }));
                    allData = [...allData, ...mapped];
                }
                setAllRawData(allData);
                applyFilter(allData, selectedType, startDate, endDate);
            } catch (error) {
                console.error('載入資料失敗', error);
                alert('載入警報資料失敗，請重整頁面');
            } finally {
                setIsLoading(false);
            }
        };
        fetchAllData();
    }, []);

    // ---- Client‑side filter ----
    const applyFilter = (data, type, start, end) => {
        let result = [...data];
        // Filter by type
        if (type !== '0') {
            const typeValues = type.split(',').map(v => parseInt(v.trim(), 10));
            result = result.filter(item => {
                const dt = Number(item.DataType);
                return typeValues.includes(dt);
            });
        }
        // Filter by date range
        if (start && end) {
            const startTime = new Date(start).getTime();
            const endTime = new Date(end).getTime();
            result = result.filter(item => {
                const itemTime = new Date(item.Timestamp).getTime();
                return itemTime >= startTime && itemTime <= endTime;
            });
        }
        // Sort newest first
        result.sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));
        setFilteredData(result);
        setCurrentPage(1);
    };

    // ---- Search button click ----
    const handleSearchClick = () => {
        setIsLoading(true);
        setTimeout(() => {
            applyFilter(allRawData, selectedType, startDate, endDate);
            setIsLoading(false);
        }, 100);
    };

    // ---- Pagination helpers ----
    const getCurrentPageData = () => {
        const startIdx = (currentPage - 1) * itemsPerPage;
        const endIdx = startIdx + itemsPerPage;
        return filteredData.slice(startIdx, endIdx);
    };

    // ---- Export to Excel ----
    const exportToExcel = () => {
        if (filteredData.length === 0) {
            alert('沒有資料可匯出');
            return;
        }
        const exportData = filteredData.map(item => ({
            '警報名稱': item.Label,
            '警報狀態': item.Status,
            '數據類型': getDataTypeName(item.DataType),
            '創建時間': item.Timestamp?.replace('T', ' ')
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '警報記錄');
        XLSX.writeFile(workbook, `AlertsData_${new Date().toISOString().slice(0, 19)}.xlsx`);
    };

    // ---- Data type mapping (matching your old values) ----
    const getDataTypeName = (dataTypeValue) => {
        const type = Number(dataTypeValue);
        switch (type) {
            case 2: return '門口監察';
            case 4: return '洗手間監察';
            case 6: case 7: case 12: return '漏水監察';
            case 9: return '垃圾/回收桶容量監察';
            case 13: return '控制面板監察';
            case 16: return '道路管理';
            case 20: return '鼠蹤檢測監察';
            default: return `類型 ${dataTypeValue}`;
        }
    };

    const getTypeIcon = (type) => {
        const t = Number(type);
        if (t === 2) return '🚪';
        if (t === 4) return '🚻';
        if (t === 6 || t === 7 || t === 12) return '💧';
        if (t === 9) return '🗑️';
        if (t === 13) return '🎛️';
        if (t === 16) return '🛣️';
        if (t === 20) return '🐭';
        return '📡';
    };

    const formatTimestamp = (isoString) => isoString?.replace('T', ' ');

    // ---- Pagination component ----
    const Pagination = () => {
        const totalPages = Math.ceil(filteredData.length / itemsPerPage);
        if (totalPages <= 1) return null;

        const getPaginationGroup = () => {
            let start = Math.floor((currentPage - 1) / 5) * 5;
            return new Array(5).fill().map((_, idx) => start + idx + 1).filter(p => p <= totalPages);
        };

        const handlePageInput = (e) => {
            let page = Number(e.target.value);
            if (!isNaN(page) && page >= 1 && page <= totalPages) {
                setCurrentPage(page);
            }
            e.target.value = '';
        };

        return (
            <div className='fashion-pagination'>
                <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                >
                    <FaChevronLeft /> 上一頁
                </button>
                <div className="pagination-numbers">
                    {getPaginationGroup().map((item, idx) => (
                        <button
                            key={idx}
                            className={`pagination-number ${item === currentPage ? 'active' : ''}`}
                            onClick={() => setCurrentPage(item)}
                        >
                            {item}
                        </button>
                    ))}
                </div>
                <div className="page-jump">
                    <input
                        type="number"
                        min={1}
                        max={totalPages}
                        placeholder="頁碼"
                        onKeyPress={(e) => e.key === 'Enter' && handlePageInput(e)}
                        onBlur={handlePageInput}
                        style={{ width: '70px', margin: '0 8px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <span> / {totalPages} 頁</span>
                </div>
                <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                >
                    下一頁 <FaChevronRight />
                </button>
            </div>
        );
    };

    // ---- Table ----
    const TableList = () => {
        const pageData = getCurrentPageData();
        return (
            <div className="Table_maindiv">
                {isLoading ? (
                    <div className="loading-shimmer">載入中...</div>
                ) : (
                    <table className="fashion-table">
                        <thead>
                            <tr>
                                <th>警報名稱</th>
                                <th>警報狀態</th>
                                <th>數據類型</th>
                                <th>創建時間</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageData.length > 0 ? (
                                pageData.map((product, idx) => (
                                    <tr className="fashion-table-row" key={idx}>
                                        <td>
                                            <div className="alarm-name-cell">
                                                <span className="alarm-icon">⚠️</span>
                                                <span>{product.Label}</span>
                                            </div>
                                        </td>
                                        <td><span className="status-badge">{product.Status}</span></td>
                                        <td>
                                            <span className="type-badge">
                                                {getTypeIcon(product.DataType)} {getDataTypeName(product.DataType)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="timestamp-cell">
                                                <FaCalendarAlt className="timestamp-icon" />
                                                <span>{formatTimestamp(product.Timestamp)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="4" className="no-data-cell">暫無數據</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    // ---- Main render ----
    return (
        <div className="fashion-dashboard">
            <div className="filter-section">
                <div className="filter-group">
                    <label className="filter-label"><FaFilter /> 警報類型</label>
                    <select className="fashion-select" onChange={(e) => setSelectedType(e.target.value)} value={selectedType}>
                        <option value="0">所有警報</option>
                        <option value="16">🛣️ 道路管理</option>
                        <option value="2">🚪 門口監察</option>
                        <option value="4">🚻 洗手間監察</option>
                        <option value="6,7,12">💧 漏水監察</option>
                        <option value="9">🗑️ 垃圾/回收桶容量監察</option>
                        <option value="13">🎛️ 控制面板監察</option>
                        <option value="20">🐭 鼠蹤檢測監察</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label className="filter-label"><FaCalendarAlt /> 起始時間</label>
                    <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd-MM-yyyy HH:mm"
                        className="fashion-datepicker"
                        popperProps={{ strategy: "fixed", modifiers: [{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "flip", enabled: false }] }}
                        popperContainer={({ children }) => createPortal(children, document.body)}
                    />
                </div>
                <div className="filter-group">
                    <label className="filter-label"><FaCalendarAlt /> 結束時間</label>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd-MM-yyyy HH:mm"
                        className="fashion-datepicker"
                        popperProps={{ strategy: "fixed", modifiers: [{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "flip", enabled: false }] }}
                        popperContainer={({ children }) => createPortal(children, document.body)}
                    />
                </div>
                <button className="search-btn" onClick={handleSearchClick}><FaSearch /> 搜尋</button>
                <button className="export-btn" onClick={exportToExcel}><FaDownload /> 匯出報表</button>
            </div>
            <div className="table-section"><TableList /></div>
            <div className="pagination-section"><Pagination /></div>
        </div>
    );
}